import { useState, useEffect, useRef } from "react";
import { taxConfigApi } from "../api/api";
import "./common.css";
import { useResizableColumns } from "../hooks/useResizableColumns";
import { usePagination } from "../hooks/usePagination";
import Pagination        from "../components/Pagination";
import RvDataCard, { RvDataCardList } from "../components/RvDataCard";

/* ── Toast ── */
function Toast({ message, type, onClose }) {
    useEffect(() => {
        const t = setTimeout(onClose, 3200);
        return () => clearTimeout(t);
    }, [onClose]);
    return (
        <div className={`rv-toast rv-toast--${type}`}>
            <span className="rv-toast-icon">{type === "success" ? <CheckIcon /> : <AlertIcon />}</span>
            <span className="rv-toast-msg">{message}</span>
            <button className="rv-toast-close" onClick={onClose}><CloseIcon /></button>
        </div>
    );
}
function CalIcon2() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /><line x1="12" y1="15" x2="12" y2="19" /><line x1="10" y1="17" x2="14" y2="17" /></svg>; }
/* ── Confirm Dialog ── */
function ConfirmDialog({ name, onConfirm, onCancel }) {
    return (
        <div className="rv-dialog-backdrop">
            <div className="rv-dialog">
                <div className="rv-dialog-icon"><TrashIcon /></div>
                <h3 className="rv-dialog-title">Delete Tax Config?</h3>
                <p className="rv-dialog-desc">
                    <strong>{name}</strong> will be permanently removed. This action cannot be undone.
                </p>
                <div className="rv-dialog-actions">
                    <button className="rv-btn rv-btn--ghost" onClick={onCancel}>Cancel</button>
                    <button className="rv-btn rv-btn--danger" onClick={onConfirm}>Delete</button>
                </div>
            </div>
        </div>
    );
}

/* ── Tax Config Form Modal ── */
function TaxModal({ initial, onSave, onClose, saving, isNewEffdt }) {
    const isEdit = !!initial?.taxConfigID;

    const todayISO = new Date().toISOString().split("T")[0];

    const [form, setForm] = useState({
        taxName: initial?.taxName ?? "",
        taxPercentage: initial?.taxPercentage != null ? String(initial.taxPercentage) : "",
        effdt: initial?.effdt ? initial.effdt.split("T")[0] : todayISO,
        isActive: initial?.isActive ?? true,
    });
    const [errors, setErrors] = useState({});

    const validate = () => {
        const e = {};
        if (!form.taxName.trim()) e.taxName = "Tax name is required.";
        if (form.taxPercentage === "") e.taxPercentage = "Tax percentage is required.";
        else if (isNaN(Number(form.taxPercentage)) || Number(form.taxPercentage) < 0 || Number(form.taxPercentage) > 100)
            e.taxPercentage = "Enter a valid percentage (0–100).";
        if (!form.effdt) e.effdt = "Effective date is required.";
        return e;
    };

    const handleSubmit = () => {
        const e = validate();
        if (Object.keys(e).length) { setErrors(e); return; }
        onSave({
            ...initial,
            taxName: form.taxName.trim(),
            taxPercentage: Number(form.taxPercentage),
            effdt: form.effdt,
            isActive: form.isActive,
        });
    };

    const field = (key, label, placeholder, type = "text", extra = {}) => (
        <div className="rv-field">
            <label className="rv-label">{label} <span className="rv-required">*</span></label>
            <input
                className={`rv-input ${errors[key] ? "rv-input--err" : ""}`}
                placeholder={placeholder}
                type={type}
                value={form[key]}
                onChange={e => { setForm(f => ({ ...f, [key]: e.target.value })); setErrors(v => ({ ...v, [key]: "" })); }}
                {...extra}
            />
            {errors[key] && <p className="rv-field-err">{errors[key]}</p>}
        </div>
    );

    return (
        <div className="rv-dialog-backdrop">
            <div className="rv-dialog rv-dialog--form">
                <div className="rv-modal-header">
                    <div className="rv-modal-icon"><TaxIcon /></div>
                    <div>
                        <h3 className="rv-modal-title">
                            {isNewEffdt ? "Add Effective Date" : isEdit ? "Edit Tax Config" : "New Tax Config"}
                        </h3>
                        <p className="rv-modal-sub">
                            {isNewEffdt
                                ? "Set a new effective date for this tax config. A new history entry will be created."
                                : isEdit
                                    ? "Update the tax config details below."
                                    : "Fill in details to create a new tax configuration."}
                        </p>
                    </div>
                    <button className="rv-modal-close" onClick={onClose}><CloseIcon /></button>
                </div>

                <div className="rv-modal-body">

                    {/* Tax Name */}
                    {isNewEffdt ? (
                        <div className="rv-field">
                            <label className="rv-label">Tax Name</label>
                            <input
                                className="rv-input"
                                value={form.taxName}
                                disabled
                                style={{ opacity: 0.5, cursor: "not-allowed" }}
                            />
                        </div>
                    ) : (
                        field("taxName", "Tax Name", "e.g. GST, IGST, VAT")
                    )}

                    {/* Percentage + Effective Date — side by side */}
                    <div className="rv-field-row-2col">
                        {field("taxPercentage", "Tax Percentage (%)", "e.g. 18", "number", { min: 0, max: 100, step: "0.01" })}

                        <div className="rv-field">
<label className="rv-label">
  Effective Date {!isEdit && <span className="rv-required">*</span>}
</label>
                            {isEdit ? (
                                <>
                                    <p className="rv-field-hint rv-effdt-hint">
                                        <CalIcon /> Effective date cannot be changed on edit
                                    </p>
                                    <input
                                        className="rv-input rv-input--date"
                                        type="date"
                                        value={form.effdt}
                                        disabled
                                        style={{ opacity: 0.5, cursor: "not-allowed" }}
                                    />
                                </>
                            ) : (
                                <>
                                    <input
                                        className={`rv-input rv-input--date ${errors.effdt ? "rv-input--err" : ""}`}
                                        type="date"
                                        value={form.effdt}
                                        onChange={e => { setForm(f => ({ ...f, effdt: e.target.value })); setErrors(v => ({ ...v, effdt: "" })); }}
                                    />
                                    {errors.effdt && <p className="rv-field-err">{errors.effdt}</p>}
                                </>
                            )}
                        </div>
                    </div>

                    {/* Active toggle */}
                    <div className="rv-field rv-field--row">
                        <div>
                            <label className="rv-label">Status</label>
                            <p className="rv-field-hint">Whether this tax config is active</p>
                        </div>
                        <button
                            type="button"
                            className={`rv-toggle ${form.isActive ? "rv-toggle--on" : ""}`}
                            onClick={() => setForm(f => ({ ...f, isActive: !f.isActive }))}
                        >
                            <span className="rv-toggle-knob" />
                        </button>
                    </div>

                </div>

                <div className="rv-modal-footer">
                    <button className="rv-btn rv-btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
                    <button className="rv-btn rv-btn--primary" onClick={handleSubmit} disabled={saving}>
                        {saving ? <span className="rv-spinner" /> : null}
                        {isEdit ? "Update" : "Create"}
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ── History Panel ── */
function HistoryPanel({ record, history: historyList, onClose }) {
    const history = (historyList || []).slice().sort(
        (a, b) => new Date(b.effdt) - new Date(a.effdt)
    );

    const fmt = (d) => {
        if (!d) return "—";
        return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    };

    return (
        <div className="rv-dialog-backdrop" onClick={onClose}>
            <div className="rv-history-panel" onClick={e => e.stopPropagation()}>
                <div className="rv-history-header">
                    <div className="rv-history-title-wrap">
                        <div className="rv-history-icon"><HistoryIcon /></div>
                        <div>
                            <h3 className="rv-history-title">Effective Date History</h3>
                            <p className="rv-history-sub">{record.taxName} · {record.taxPercentage}%</p>
                        </div>
                    </div>
                    <button className="rv-modal-close" onClick={onClose}><CloseIcon /></button>
                </div>

                <div className="rv-history-body">
                    {history.length === 0 ? (
                        <div className="rv-history-empty">
                            <p>No history records available.</p>
                        </div>
                    ) : (
                        <div className="rv-history-timeline">
                            {history.map((h, i) => (
                                <div key={h.taxConfigID ?? i} className={`rv-history-entry ${i === 0 ? "rv-history-entry--current" : ""}`}>
                                    <div className="rv-history-line-wrap">
                                        <div className={`rv-history-dot ${i === 0 ? "rv-history-dot--current" : ""}`} />
                                        {i < history.length - 1 && <div className="rv-history-connector" />}
                                    </div>
                                    <div className="rv-history-content">
                                        <div className="rv-history-row">
                                            <span className="rv-history-date">{fmt(h.effdt)}</span>
                                            {i === 0 && <span className="rv-history-current-badge">Current</span>}
                                        </div>
                                        <div className="rv-history-meta">
                                            <span className="rv-history-pct">{h.taxPercentage}%</span>
                                            <span className={`rv-history-status ${h.isActive ? "rv-history-status--active" : "rv-history-status--inactive"}`}>
                                                <span className="rv-status-dot" />
                                                {h.isActive ? "Active" : "Inactive"}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

/* ══════════════════════════════════════
   MAIN TAX CONFIG PAGE
══════════════════════════════════════ */
export default function TaxConfigPage() {
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [filterStatus, setFilterStatus] = useState("all");

    const [modal, setModal] = useState(null);
    const [saving, setSaving] = useState(false);
    const [confirm, setConfirm] = useState(null);
    const [toast, setToast] = useState(null);
    const [history, setHistory] = useState(null); // { record, history[] }

    /* ── Resizable columns ── */
    const tableRef = useRef(null);
    const [tableReady, setTableReady] = useState(false);
    // Columns: #, Name, %, Effective Date, Status, Actions
    useResizableColumns(tableRef, tableReady, [50, 200, 120, 150, 120, 120]);

    /* ── Fetch ── */
    const fetchAll = async () => {
        try {
            setLoading(true);
            setTableReady(false);
            const data = await taxConfigApi.getAll();
            const list = Array.isArray(data) ? data : data?.data ?? [];
            setRecords(list);
        } catch (err) {
            showToast(err.message || "Failed to load tax configurations.", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchAll(); }, []);
    useEffect(() => { if (!loading) setTableReady(true); }, [loading]);

    const showToast = (message, type = "success") => setToast({ message, type });

    /* ── Derive history for a given taxName ──
       The API returns a flat list; we group by taxName
       to build a virtual history timeline. */
    const buildHistory = (record) => {
        const related = records
            .filter(r => r.taxName === record.taxName)
            .sort((a, b) => new Date(b.effdt) - new Date(a.effdt));
        return { record, history: related };
    };

    /* ── Latest per taxName (for main table view) ── */
    const latestByName = (() => {
        const map = new Map();
        records.forEach(r => {
            const existing = map.get(r.taxName);
            if (!existing || new Date(r.effdt) > new Date(existing.effdt)) {
                map.set(r.taxName, r);
            }
        });
        return Array.from(map.values());
    })();

    /* ── History count per taxName ── */
    const historyCount = (taxName) =>
        records.filter(r => r.taxName === taxName).length;

    /* ── Filtered list ── */
    const filtered = latestByName.filter(r => {
        const matchSearch =
            r.taxName?.toLowerCase().includes(search.toLowerCase()) ||
            String(r.taxPercentage).includes(search);
        const matchStatus =
            filterStatus === "all" ||
            (filterStatus === "active" && r.isActive) ||
            (filterStatus === "inactive" && !r.isActive);
        return matchSearch && matchStatus;
    });
    const {
  paged, page, totalPages, pageSize,
  setPage, setPageSize,
  pageStart, pageEnd, totalItems,
} = usePagination(filtered, 10);

    /* ── Save ── */
    const handleSave = async (formData) => {
        setSaving(true);
        try {
            if (formData.taxConfigID) {
                await taxConfigApi.update(formData);
                showToast("Tax config updated successfully.");
            } else {
                await taxConfigApi.create(formData);
                showToast("Tax config created successfully.");
            }
            setModal(null);
            fetchAll();
        } catch (err) {
            showToast(err.message || "Something went wrong.", "error");
        } finally {
            setSaving(false);
        }
    };

    /* ── Delete ── */
    const handleDelete = async () => {
        if (!confirm) return;
        try {
            await taxConfigApi.remove(confirm.id);
            showToast("Tax config deleted.");
            setConfirm(null);
            fetchAll();
        } catch (err) {
            showToast(err.message || "Delete failed.", "error");
        }
    };

    /* ── Format date ── */
    const fmt = (d) => {
        if (!d) return "—";
        return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    };

    return (
        <div className="rv-tax-page">

            {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
            {confirm && (
                <ConfirmDialog
                    name={confirm.name}
                    onConfirm={handleDelete}
                    onCancel={() => setConfirm(null)}
                />
            )}
            {modal && (
                <TaxModal
                    initial={modal.data}
                    onSave={handleSave}
                    onClose={() => setModal(null)}
                    saving={saving}
                    isNewEffdt={modal.isNewEffdt}
                />
            )}
            {history && (
                <HistoryPanel
                    record={history.record}
                    history={history.history}
                    onClose={() => setHistory(null)}
                />
            )}

            {/* ── Topbar ── */}
            <div className="rv-tax-topbar">
                <div className="rv-tax-topbar-left">
                    <h2 className="rv-tax-heading">Tax Configuration</h2>
                    <span className="rv-tax-count">{latestByName.length} total</span>
                </div>
                <button className="rv-btn rv-btn--primary" onClick={() => setModal({ data: {} })}>
                    <PlusIcon /> New Tax Config
                </button>
            </div>

            {/* ── Filters ── */}
            <div className="rv-tax-filters">
                <div className="rv-tax-search">
                    <SearchIcon />
                    <input
                        placeholder="Search by name or percentage…"
                        value={search}
                        onChange={e => { setSearch(e.target.value); setPage(1); }}
                    />
                    {search && (
                        <button className="rv-tax-search-clear" onClick={() => { setSearch(""); setPage(1); }}>
                            <CloseIcon />
                        </button>
                    )}
                </div>

                <div className="rv-filter-tabs">
                    {["all", "active", "inactive"].map(s => (
                        <button
                            key={s}
                            className={`rv-filter-tab ${filterStatus === s ? "rv-filter-tab--active" : ""}`}
                            onClick={() => { setFilterStatus(s); setPage(1); }}
                        >
                            {s.charAt(0).toUpperCase() + s.slice(1)}
                        </button>
                    ))}
                </div>
            </div>

            {/* ── Table card ── */}
            <div className="rv-card rv-tax-card">
                {loading ? (
                    <div className="rv-tax-empty">
                        <div className="rv-loader" />
                        <p>Loading tax configurations…</p>
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="rv-tax-empty">
                        <div className="rv-empty-icon"><TaxIcon /></div>
                        <p className="rv-empty-title">No tax configs found</p>
                        <p className="rv-empty-sub">
                            {search ? "Try a different search term." : "Create your first tax configuration to get started."}
                        </p>
                        {!search && (
                            <button className="rv-btn rv-btn--primary rv-mt-12" onClick={() => setModal({ data: {} })}>
                                <PlusIcon /> New Tax Config
                            </button>
                        )}
                    </div>
                ) : (
                    <>
                        {/* Desktop Table View */}
                        <div className="rv-desktop-table-view">
                            <div className="rv-table-wrap rv-tax-table-wrap">
                                <table className="rv-table rv-tax-table" ref={tableRef}>
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Tax Name</th>
                                            <th>Percentage</th>
                                            <th>Effective Date</th>
                                            <th>Status</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paged.map((r, i) => {
                                            const count = historyCount(r.taxName);
                                            return (
                                                <tr key={r.taxConfigID} className="rv-tax-row">
                                                   <td className="rv-tax-idx">{pageStart + i}</td>
                                                    <td>
                                                        <div className="rv-tax-name-cell">
                                                            <span className="rv-tax-name">{r.taxName}</span>
                                                            {count > 1 && (
                                                                <button
                                                                    className="rv-history-chip"
                                                                    title="View effective date history"
                                                                    onClick={() => setHistory(buildHistory(r))}
                                                                >
                                                                    <HistoryIcon />
                                                                    {count} entries
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className="rv-pct-badge">
                                                            <span className="rv-pct-val">{r.taxPercentage}</span>
                                                            <span className="rv-pct-sym">%</span>
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div className="rv-effdt-cell">
                                                            <span className="rv-effdt-icon"><CalIcon /></span>
                                                            <span className="rv-effdt-val">{fmt(r.effdt)}</span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className={`rv-status-pill ${r.isActive ? "rv-status-pill--active" : "rv-status-pill--inactive"}`}>
                                                            <span className="rv-status-dot" />
                                                            {r.isActive ? "Active" : "Inactive"}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div className="rv-actions">
                                                            {count > 1 && (
                                                                <button
                                                                    className="rv-action-btn rv-action-btn--hist"
                                                                    title="View history"
                                                                    onClick={() => setHistory(buildHistory(r))}
                                                                >
                                                                    <HistoryIcon />
                                                                </button>
                                                            )}
                                                            <button
                                                                className="rv-action-btn rv-action-btn--edit"
                                                                title="Edit"
                                                                onClick={() => setModal({ data: r })}
                                                            >
                                                                <EditIcon />
                                                            </button>
                                                            <button
                                                                className="rv-action-btn rv-action-btn--hist"
                                                                title="Add new effective date"
                                                                onClick={() => setModal({
                                                                    data: { ...r, taxConfigID: 0, effdt: new Date().toISOString().split("T")[0] },
                                                                    isNewEffdt: true
                                                                })}
                                                            >
                                                                <CalIcon2 />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Mobile Cards View */}
                        <div className="rv-mobile-cards-view">
                            <RvDataCardList
                                loading={loading}
                                items={paged}
                                emptyIcon={<TaxIcon />}
                                emptyTitle="No tax configurations found"
                                emptySubtitle={search ? "Try a different search term." : "Create your first tax configuration to get started."}
                                emptyAction={
                                    !search && (
                                        <button className="rv-btn rv-btn--primary" onClick={() => setModal({ data: {} })}>
                                            <PlusIcon /> New Tax Config
                                        </button>
                                    )
                                }
                                renderCard={(r, i) => {
                                    const count = historyCount(r.taxName);
                                    return (
                                        <RvDataCard
                                            key={r.taxConfigID}
                                            index={pageStart + i}
                                            title={r.taxName}
                                            subtitle={`${r.taxPercentage}% Tax Rate`}
                                            badge={
                                                <span className={`rv-status-pill ${r.isActive ? "rv-status-pill--active" : "rv-status-pill--inactive"}`}>
                                                    <span className="rv-status-dot" />
                                                    {r.isActive ? "Active" : "Inactive"}
                                                </span>
                                            }
                                            tag={
                                                count > 1 ? (
                                                    <button
                                                        className="rv-history-chip"
                                                        title="View effective date history"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setHistory(buildHistory(r));
                                                        }}
                                                    >
                                                        <HistoryIcon /> {count} entries
                                                    </button>
                                                ) : null
                                            }
                                            fields={[
                                                { label: "Tax Percentage", value: <span className="rv-pct-val" style={{ fontWeight: 700, fontSize: 13 }}>{r.taxPercentage}%</span> },
                                                { label: "Effective Date", value: fmt(r.effdt) },
                                            ]}
                                            actions={
                                                <div className="rv-actions" onClick={e => e.stopPropagation()}>
                                                    {count > 1 && (
                                                        <button
                                                            className="rv-action-btn rv-action-btn--hist"
                                                            title="View history"
                                                            onClick={() => setHistory(buildHistory(r))}
                                                        >
                                                            <HistoryIcon />
                                                        </button>
                                                    )}
                                                    <button
                                                        className="rv-action-btn rv-action-btn--edit"
                                                        title="Edit"
                                                        onClick={() => setModal({ data: r })}
                                                    >
                                                        <EditIcon />
                                                    </button>
                                                    <button
                                                        className="rv-action-btn rv-action-btn--hist"
                                                        title="Add new effective date"
                                                        onClick={() => setModal({
                                                            data: { ...r, taxConfigID: 0, effdt: new Date().toISOString().split("T")[0] },
                                                            isNewEffdt: true
                                                        })}
                                                    >
                                                        <CalIcon2 />
                                                    </button>
                                                </div>
                                            }
                                        />
                                    );
                                }}
                            />
                        </div>

                        {/* Shared Pagination */}
                        <Pagination
                            page={page}
                            totalPages={totalPages}
                            pageSize={pageSize}
                            setPage={setPage}
                            setPageSize={setPageSize}
                            pageStart={pageStart}
                            pageEnd={pageEnd}
                            totalItems={totalItems}
                        />
                    </>
                )}
            </div>

            {/* ── Resize hint (Desktop Only) ── */}
            {!loading && filtered.length > 0 && (
                <p className="rv-resize-hint rv-desktop-table-view">
                    <GripIcon /> Drag column borders to resize
                </p>
            )}
        </div>
    );
}

/* ── Icons ── */
function PlusIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>; }
function SearchIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>; }
function EditIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>; }
function TrashIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>; }
function CloseIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>; }
function CheckIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>; }
function AlertIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>; }
function TaxIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="9" y1="15" x2="15" y2="9" /><circle cx="9.5" cy="9.5" r="0.5" fill="currentColor" /><circle cx="14.5" cy="14.5" r="0.5" fill="currentColor" /></svg>; }
function HistoryIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 .49-3.51" /><polyline points="12 7 12 12 15 15" /></svg>; }
function CalIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" style={{ width: 12, height: 12, display: 'inline', verticalAlign: 'middle' }}><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>; }
function GripIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" style={{ width: 13, height: 13 }}><circle cx="9" cy="5" r="1" fill="currentColor" /><circle cx="9" cy="12" r="1" fill="currentColor" /><circle cx="9" cy="19" r="1" fill="currentColor" /><circle cx="15" cy="5" r="1" fill="currentColor" /><circle cx="15" cy="12" r="1" fill="currentColor" /><circle cx="15" cy="19" r="1" fill="currentColor" /></svg>; }